/**
 * Tour tooltip card with navigation controls
 */

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TooltipPosition } from "../tour-steps";
import { getTooltipPosition } from "./tooltip-utils";

type TourTooltipProps = {
  title: string;
  description: string;
  targetSelector: string;
  preferredPosition: TooltipPosition;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  isVisible: boolean;
  padding?: number;
};

export const TourTooltip = ({
  title,
  description,
  targetSelector,
  preferredPosition,
  currentStep,
  totalSteps,
  onNext,
  onBack,
  onSkip,
  isVisible,
  padding = 8,
}: TourTooltipProps) => {
  const [position, setPosition] = useState({ 
    top: 0, 
    left: 0, 
    placement: preferredPosition as "top" | "bottom" | "left" | "right" | "center"
  });

  // Update position when any relevant prop changes
  useEffect(() => {
    if (!isVisible) return;

    const updatePosition = () => {
      const pos = getTooltipPosition(targetSelector, preferredPosition);
      setPosition({ top: pos.top, left: pos.left, placement: pos.position });
    };

    // Calculate immediately
    updatePosition();

    const handleUpdate = () => {
      requestAnimationFrame(updatePosition);
    };

    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);

    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
    };
  }, [isVisible, targetSelector, preferredPosition, padding]);

  if (!isVisible) return null;

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  
  // For center position (completion step), use special handling
  const isCentered = preferredPosition === "center" || !targetSelector;

  // Calculate styles based on position
  let style: React.CSSProperties;
  if (isCentered) {
    // Use viewport centering with flexbox-like positioning
    style = {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    };
  } else {
    // Determine transform based on actual position
    let transform = "";
    if (position.placement === "top") {
      transform = "translate(-50%, -100%)";
    } else if (position.placement === "bottom") {
      transform = "translateX(-50%)";
    } else if (position.placement === "left") {
      transform = "translate(-100%, -50%)";
    } else if (position.placement === "right") {
      transform = "translateY(-50%)";
    }
    style = {
      top: position.top,
      left: position.left,
      transform,
    };
  }

  return (
    <div
      className={cn(
        "fixed z-[10000] w-[360px] max-w-[calc(100vw-40px)]",
        "bg-card border border-border rounded-xl shadow-2xl",
        "animate-in fade-in-0 zoom-in-95 duration-300"
      )}
      style={style}
    >
      {/* Green accent bar */}
      <div className="h-1 w-full bg-gradient-to-r from-transparent via-emerald-500 to-transparent rounded-t-xl" />

      {/* Skip button */}
      <button
        onClick={onSkip}
        className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        aria-label="Skip tour"
      >
        <X className="size-4" />
      </button>

      {/* Content */}
      <div className="p-5 pt-4">
        <h3 className="text-lg font-semibold text-foreground mb-2 pr-6">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">{description}</p>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-1.5 mb-4">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <div
              key={index}
              className={cn(
                "h-1.5 rounded-full transition-all duration-300",
                index === currentStep
                  ? "w-6 bg-emerald-500"
                  : "w-1.5 bg-muted-foreground/30"
              )}
            />
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={onBack}
            disabled={isFirstStep}
            className="flex-1 h-10"
          >
            <ChevronLeft className="size-4 mr-1" />
            Back
          </Button>
          <Button
            onClick={onNext}
            className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {isLastStep ? "Finish" : "Next"}
            {!isLastStep && <ChevronRight className="size-4 ml-1" />}
          </Button>
        </div>

        {/* Skip link */}
        <button
          onClick={onSkip}
          className="w-full mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip tour
        </button>
      </div>
    </div>
  );
};

